import { useEffect, useState, type FormEvent } from 'react'
import { Starfield } from '../components/Starfield'
import { LANG_NAMES, applyDocumentLang, makeT, type TKey } from '../i18n'
import { authError, goToGoogleLogin, emailLogin, emailRegister, AuthError } from '../lib/auth'
import { hasStoredProfile, loadProfile } from '../lib/storage'
import { LANGS, type Lang } from '../data/cards'

/** Код ошибки от сервера → строка интерфейса. */
const ERR_KEY: Record<string, TKey> = {
  bad_email: 'loginBadEmail',
  bad_password: 'loginBadPass',
  email_taken: 'loginTaken',
  bad_credentials: 'loginBadCreds',
}

/** Язык для экрана входа: сохранённый выбор, иначе адрес, иначе по умолчанию az. */
function initialLang(): Lang {
  try {
    // Все четыре языка, а не ru/az: с прежней проверкой ссылка ?lang=tr
    // молча приводила турка на азербайджанский.
    const url = new URLSearchParams(window.location.search).get('lang')
    if (url && (LANGS as string[]).includes(url)) return url as Lang
  } catch {
    /* нет доступа к адресу — не беда */
  }
  if (hasStoredProfile()) return loadProfile().lang
  return 'az'
}

/**
 * Экран входа PWA-версии (вне Telegram). Пока — только Google; вход по почте
 * добавится следом. Уводит на серверную ручку `/auth/google/start`, откуда
 * человек вернётся уже с сессией.
 */
/**
 * Экран входа.
 *
 * Раньше он стоял воротами перед приложением; теперь это обычный экран, на
 * который приходят по своей воле — из профиля или с экрана звёзд. Отсюда и
 * `onDone`: когда вход удался, вернуть человека туда, откуда он пришёл, а не
 * бросить на главной. Без пропса ведёт себя как прежде — так он и работает,
 * когда сессию завести не удалось и показывать больше нечего.
 */
export function Login({ onDone }: { onDone?: () => void } = {}) {
  const [lang, setLang] = useState<Lang>(initialLang)
  const t = makeT(lang)

  // Экран входа рендерится вне `SirrProvider`, поэтому язык документа он ставит
  // сам — иначе до входа регистр приводится по правилам из `index.html`
  // (`lang="ru"`), и азербайджанское `Bəli` даёт `BƏLI` вместо `BƏLİ`.
  useEffect(() => {
    applyDocumentLang(lang)
  }, [lang])

  const [mode, setMode] = useState<'login' | 'register'>('login')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [name, setName] = useState('')
  const [busy, setBusy] = useState(false)
  /** Список языков раскрыт. Свёрнутый — одна кнопка в углу. */
  const [langOpen, setLangOpen] = useState(false)
  // Ошибка формы: либо код от сервера (через ERR_KEY), либо общий текст.
  const [error, setError] = useState<string | null>(authError() ? t('loginError') : null)

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    setBusy(true)
    try {
      if (mode === 'register') await emailRegister(email, password, name, lang)
      else await emailLogin(email, password)
      // Вход удался. Если экран открыт изнутри приложения — возвращаем туда,
      // откуда пришли; если он стоит вместо приложения (сессию завести не
      // удалось), возврата нет, и корень перерисуется сам по 'sirr-authed'.
      onDone?.()
    } catch (err) {
      const code = err instanceof AuthError ? err.code : undefined
      setError(code && ERR_KEY[code] ? t(ERR_KEY[code]) : t('loginError'))
      setBusy(false)
    }
  }

  return (
    <div
      style={{
        position: 'relative',
        minHeight: '100dvh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        // Сверху отступ считается от безопасной зоны и кнопки языка (16 + 30 + 10):
        // на коротких экранах содержимое не помещается, прижимается к верху и
        // уходит под неё.
        padding: 'calc(56px + var(--tg-inset-top)) 34px 40px',
        gap: 22,
        background: 'var(--bg-deep)',
        overflow: 'hidden',
      }}
    >
      <Starfield count={26} />

      {/*
        Переключатель языка — кнопка в углу со списком, а не четыре кода в ряд.

        Языки берём из `LANGS`, а не списком: здесь были зашиты только `ru` и
        `az`, и человек с английским или турецким не мог выбрать свой язык до
        входа. Но четыре кода в ряд занимали всю ширину под шапкой и на экранах
        короче 16:9 налезали на эмблему — содержимое там не помещается,
        прижимается к верху и уходит под них. Замечено на Android 1080×1920.

        Кнопка-цикл вместо списка не годится (о чём и правило в CLAUDE.md про
        шапку): с четырьмя языками она не показывает выбора. Список показывает
        и текущий язык, и все остальные.
      */}
      <div style={{ position: 'absolute', top: 'calc(16px + var(--tg-inset-top))', right: 18, zIndex: 3 }}>
        <button
          onClick={() => setLangOpen((v) => !v)}
          aria-expanded={langOpen}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            background: 'rgba(18, 10, 31, 0.72)',
            color: 'var(--ink-muted)',
            border: `1px solid var(--line${langOpen ? '-strong' : ''})`,
            borderRadius: 8,
            padding: '5px 10px',
            fontFamily: 'var(--sans)',
            fontSize: 13,
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          {lang.toUpperCase()}
          <span style={{ fontSize: 8 }}>{langOpen ? '▲' : '▼'}</span>
        </button>

        {langOpen && (
          <div
            style={{
              position: 'absolute',
              top: 'calc(100% + 6px)',
              right: 0,
              minWidth: 168,
              // Непрозрачная подложка обязательна: под меню звёзды и эмблема.
              background: '#150c26',
              border: '1px solid var(--line-strong)',
              borderRadius: 12,
              overflow: 'hidden',
              boxShadow: '0 10px 28px rgba(0, 0, 0, 0.55)',
            }}
          >
            {LANGS.map((l) => (
              <button
                key={l}
                onClick={() => {
                  setLang(l)
                  setLangOpen(false)
                }}
                aria-current={l === lang}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 10,
                  width: '100%',
                  background: l === lang ? 'rgba(200, 162, 78, 0.12)' : 'transparent',
                  color: l === lang ? 'var(--gold)' : 'var(--ink-muted)',
                  border: 'none',
                  padding: '11px 14px',
                  fontFamily: 'var(--sans)',
                  fontSize: 14,
                  fontWeight: l === lang ? 600 : 400,
                  cursor: 'pointer',
                }}
              >
                {/* Название на самом языке: его узнают, не понимая текущего. */}
                {LANG_NAMES[l]}
                {/* Галочку у невыбранных прячем прозрачностью, а не убираем:
                    иначе строки разной ширины и список дёргается при выборе.
                    Для чтения с экрана она скрыта совсем: невидимую глазом
                    галочку читалка всё равно произносила, и выбранными
                    оказывались все четыре языка. Состояние несёт aria-current. */}
                <span aria-hidden style={{ color: 'var(--gold)', opacity: l === lang ? 1 : 0 }}>✓</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Закрывает список нажатием мимо. Ниже самого списка, выше остального. */}
      {langOpen && (
        <div
          onClick={() => setLangOpen(false)}
          style={{ position: 'fixed', inset: 0, zIndex: 2 }}
        />
      )}

      {/* Эмблема — как на первом экране онбординга. */}
      <div style={{ animation: 'floaty 5s ease-in-out infinite', zIndex: 1 }}>
        <div
          style={{
            width: 96,
            height: 96,
            borderRadius: '50%',
            border: '1px solid rgba(200,162,78,.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: 'var(--gold-glow)',
            background: 'radial-gradient(circle at 50% 40%, rgba(58,30,92,.7), rgba(18,10,31,.9))',
          }}
        >
          <div
            style={{
              width: 50,
              height: 50,
              border: '1px solid rgba(200,162,78,.7)',
              transform: 'rotate(45deg)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <span style={{ transform: 'rotate(-45deg)', color: 'var(--gold)', fontSize: 22 }}>✦</span>
          </div>
        </div>
      </div>

      <div style={{ zIndex: 1 }}>
        <h1 style={{ fontFamily: 'var(--serif)', color: 'var(--ink)', letterSpacing: 6, fontSize: 34, margin: 0 }}>
          SIRR
        </h1>
        <p style={{ fontFamily: 'var(--serif-body)', color: 'var(--ink-soft)', margin: '10px 0 0', fontSize: 18, maxWidth: 300 }}>
          {t('loginSub')}
        </p>
      </div>

      {error && (
        <div
          style={{
            zIndex: 1,
            color: '#f0b8b8',
            background: 'rgba(120,30,30,.25)',
            border: '1px solid rgba(200,90,90,.4)',
            borderRadius: 10,
            padding: '8px 14px',
            fontFamily: 'var(--sans)',
            fontSize: 14,
            maxWidth: 320,
          }}
        >
          {error}
        </div>
      )}

      {/* Главный путь — Telegram-бот: одна ссылка sirr.app, а способ человек
          выбирает сам. Google и почта — второстепенные варианты ниже. */}
      <a
        href="https://t.me/Sirr_tarot_bot"
        target="_blank"
        rel="noopener"
        style={{
          zIndex: 1,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 12,
          width: 280,
          maxWidth: '80vw',
          boxSizing: 'border-box',
          background: '#229ED9',
          color: '#fff',
          border: 'none',
          borderRadius: 14,
          padding: '16px 26px',
          fontFamily: 'var(--sans)',
          fontSize: 17,
          fontWeight: 700,
          textDecoration: 'none',
          boxShadow: '0 6px 20px rgba(34,158,217,.35)',
          marginTop: 6,
        }}
      >
        <TelegramGlyph />
        {t('loginTelegram')}
      </a>

      {/* Разделитель «или» */}
      <div style={{ zIndex: 1, display: 'flex', alignItems: 'center', gap: 12, width: 280, maxWidth: '80vw', color: 'var(--ink-faint)' }}>
        <span style={{ flex: 1, height: 1, background: 'var(--line)' }} />
        <span style={{ fontFamily: 'var(--sans)', fontSize: 12 }}>{t('loginOr')}</span>
        <span style={{ flex: 1, height: 1, background: 'var(--line)' }} />
      </div>

      {/* Google — второстепенно: контурная кнопка, без золотой заливки.
          Кнопка, а не ссылка: перед уходом надо успеть спросить у сервера
          одноразовый код, чтобы анонимная история не потерялась при входе. */}
      <button
        type="button"
        onClick={() => void goToGoogleLogin(lang)}
        style={{
          zIndex: 1,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 10,
          width: 280,
          maxWidth: '80vw',
          boxSizing: 'border-box',
          background: 'transparent',
          color: 'var(--ink)',
          border: '1px solid var(--line-strong)',
          borderRadius: 14,
          padding: '12px 26px',
          fontFamily: 'var(--sans)',
          fontSize: 15,
          fontWeight: 600,
          textDecoration: 'none',
        }}
      >
        <GoogleGlyph />
        {t('loginGoogle')}
      </button>

      {/* Вход/регистрация по почте */}
      <form
        onSubmit={submit}
        style={{ zIndex: 1, display: 'flex', flexDirection: 'column', gap: 10, width: 280, maxWidth: '80vw' }}
      >
        {mode === 'register' && (
          <Field placeholder={t('loginName')} value={name} onChange={setName} type="text" autoComplete="name" />
        )}
        <Field placeholder={t('loginEmail')} value={email} onChange={setEmail} type="email" autoComplete="email" />
        <Field
          placeholder={t('loginPassword')}
          value={password}
          onChange={setPassword}
          type="password"
          autoComplete={mode === 'register' ? 'new-password' : 'current-password'}
        />
        <button
          type="submit"
          disabled={busy}
          style={{
            background: 'transparent',
            color: 'var(--ink)',
            border: '1px solid var(--line-strong)',
            borderRadius: 14,
            padding: '13px 26px',
            fontFamily: 'var(--sans)',
            fontSize: 15,
            fontWeight: 600,
            cursor: busy ? 'default' : 'pointer',
            opacity: busy ? 0.6 : 1,
          }}
        >
          {mode === 'register' ? t('loginSignUp') : t('loginSignIn')}
        </button>
      </form>

      <button
        onClick={() => {
          setError(null)
          setMode((m) => (m === 'login' ? 'register' : 'login'))
        }}
        style={{
          zIndex: 1,
          background: 'transparent',
          border: 'none',
          color: 'var(--gold)',
          fontFamily: 'var(--sans)',
          fontSize: 13,
          cursor: 'pointer',
          textDecoration: 'underline',
        }}
      >
        {mode === 'login' ? t('loginToRegister') : t('loginToLogin')}
      </button>
    </div>
  )
}

/** Поле ввода в стиле приложения. */
function Field({
  placeholder,
  value,
  onChange,
  type,
  autoComplete,
}: {
  placeholder: string
  value: string
  onChange: (v: string) => void
  type: string
  autoComplete: string
}) {
  return (
    <input
      type={type}
      placeholder={placeholder}
      value={value}
      autoComplete={autoComplete}
      onChange={(e) => onChange(e.target.value)}
      style={{
        background: 'rgba(30,17,52,.5)',
        border: '1px solid var(--line)',
        borderRadius: 12,
        padding: '13px 15px',
        color: 'var(--ink)',
        fontFamily: 'var(--sans)',
        fontSize: 15,
        outline: 'none',
        width: '100%',
        boxSizing: 'border-box',
      }}
    />
  )
}

/** Бумажный самолётик Telegram, белый. */
function TelegramGlyph() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="#fff" aria-hidden="true" style={{ flexShrink: 0 }}>
      <path d="M9.78 18.65l.28-4.23 7.68-6.92c.34-.31-.07-.46-.52-.19L7.74 13.3 3.64 12c-.88-.25-.89-.86.2-1.3l15.97-6.16c.73-.33 1.43.18 1.15 1.3l-2.72 12.81c-.19.91-.74 1.13-1.5.71L12.6 16.3l-1.99 1.93c-.23.23-.42.42-.83.42z" />
    </svg>
  )
}

/** Официальная четырёхцветная «G» Google, вписанная в белый кружок. */
function GoogleGlyph() {
  return (
    <span
      style={{
        width: 26,
        height: 26,
        borderRadius: '50%',
        background: '#fff',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      <svg width="16" height="16" viewBox="0 0 48 48" aria-hidden="true">
        <path fill="#4285F4" d="M45 24c0-1.5-.1-3-.4-4.4H24v8.4h11.8c-.5 2.7-2 5-4.4 6.6v5.5h7.1C42.7 36.3 45 30.7 45 24z" />
        <path fill="#34A853" d="M24 46c6 0 11-2 14.6-5.4l-7.1-5.5c-2 1.3-4.5 2.1-7.5 2.1-5.8 0-10.7-3.9-12.5-9.2H4.2v5.7C7.8 41.1 15.3 46 24 46z" />
        <path fill="#FBBC05" d="M11.5 28c-.5-1.3-.7-2.7-.7-4s.3-2.7.7-4v-5.7H4.2C2.8 17.1 2 20.4 2 24s.8 6.9 2.2 9.7l7.3-5.7z" />
        <path fill="#EA4335" d="M24 10.8c3.3 0 6.2 1.1 8.5 3.3l6.3-6.3C35 4.1 30 2 24 2 15.3 2 7.8 6.9 4.2 14.3l7.3 5.7C13.3 14.7 18.2 10.8 24 10.8z" />
      </svg>
    </span>
  )
}
